import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FlaskConical } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground flex">
      <div className="hidden lg:flex flex-col justify-between w-[45%] border-r border-border p-12 bg-card/40">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-6 w-6 text-primary" />
          <span className="font-heading font-bold text-lg tracking-tight">Nexus R&D</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight">
            Every requirement,<br />task, and dependency.<br />
            <span className="text-primary">One command center.</span>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-md text-sm leading-relaxed">
            Requirements traceability, Gantt planning with dependencies, GitHub-style issues,
            and team discussions — built for R&D programs.
          </p>
        </div>
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Nexus R&D · Project Planning OS</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-in">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <FlaskConical className="h-6 w-6 text-primary" />
            <span className="font-heading font-bold text-lg">Nexus R&D</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold">{mode === "login" ? "Sign in" : "Create account"}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            {mode === "login" ? "Welcome back to your R&D workspace." : "New accounts join as Members."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <p data-testid="login-error-message" className="text-sm text-rose-500">{error}</p>}
            <Button type="submit" data-testid="login-submit-button" className="w-full" disabled={busy}>
              {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <button
            data-testid="toggle-auth-mode-btn"
            className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
          >
            {mode === "login" ? "No account? Register" : "Have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
